import { CheckoutArgs, POSTCheckout } from '@/apis/marketplace/requests'
import { useGetPaymentFees } from '@/apis/payments/hooks/useGetPaymentFees'
import { useAppAlert } from '@/app/AppAlertProvider'
import { Button } from '@/app/common/components/Button'
import AppDialog from '@/app/common/components/Dialog'
import { EllipsesLoader } from '@/app/common/components/EllipsesLoader'
import { Input } from '@/app/common/components/Input'
import MultiQueryPageLoader from '@/app/common/components/PageLoader/MultiQueryPageLoader'
import { currencyFormatter, parseRequestError } from '@/app/common/utilities'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import {
  clearCart,
  removeFromCart,
  updateQuantity,
} from '@/storage/applications/marketplaceCartSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { RootState } from '@/storage/store'
import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { SubmitButton } from '../../family_application/student_details/shared/submit-button'
import { useStudentPurses } from '../../providers/api/usePaymentProviders'
import {
  PurseFormData,
  PurseSelectionForm,
} from '../../providers/pages/choose_purses/choose_purses_form'
import { getTotalAmountToBePaidByApplicant } from '@/app/common/payment.utils'
import { getTotalServiceFeeToBePaidByApplicant } from '@/app/common/utils/payment/utils'

type CartProps = {
  hideCart: () => void
}

const Cart = ({ hideCart }: CartProps) => {
  const programId = useSelector(selectProgramID)
  const cart = useSelector((state: RootState) => state.MarketplaceCart)
  const dispatch = useDispatch()
  const { fireAlert } = useAppAlert()
  const { t: translate } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (payload: CheckoutArgs) => POSTCheckout(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['studentPurses'] })
      fireAlert({
        variant: 'success',
        message: translate('order_placed_successfully'),
      })
      dispatch(clearCart())
      hideCart()
    },

    onError: (error) => {
      const parsedError = parseRequestError(error)
      fireAlert({
        variant: 'error',
        message: parsedError.message,
      })
    },
  })
  const application = useSelector(selectFamilyApplication)
  const purseQuery = useStudentPurses(
    {
      program_id: programId,
      application_id: application.application_id,
    },
    {
      placeholderData: keepPreviousData,
    }
  )

  const [showPurseSelection, setShowPurseSelection] = useState(false)

  const handleRemoveItem = (id: number) => {
    dispatch(removeFromCart(id))
  }

  const handleQuantityChange = (id: number, quantity: number) => {
    dispatch(updateQuantity({ id, quantity }))
  }

  const handleCheckout = () => {
    setShowPurseSelection(true)
  }

  const onClosePurseSelection = () => {
    setShowPurseSelection(false)
  }

  const paymentFeeQuery = useGetPaymentFees({
    base_amount: cart.total,
    program_id: programId,
    placeholderData: keepPreviousData,
  })

  const onSubmit = (formData: PurseFormData) => {
    if (!formData?.purses) return
    fireAlert({
      variant: 'warning',
      titleText: translate('checkout'),
      message: translate('do_you_want_to_proceed'),
      confirmBtn: true,
      confirmBtnText: translate('yes'),
      denyBtn: true,
      denyBtnText: translate('no'),
    }).then(({ confirmed }) => {
      if (confirmed) {
        setShowPurseSelection(false)
        const payload = {
          program_id: programId,
          items: cart.items.map((item) => ({
            id: item.id,
            quantity: item.quantity,
          })),
          purses: formData.purses.map((purse) => ({
            id: purse.purse_id,
            amount: purse.amount,
          })),
        }
        mutation.mutate(payload)
      }
    })
  }

  return (
    <MultiQueryPageLoader queries={[purseQuery, paymentFeeQuery] as const}>
      {([purse, paymentFee]) => {
        const totalAmount = getTotalAmountToBePaidByApplicant(paymentFee)

        return (
          <div className="bg-white lg:px-4">
            {cart.items.length === 0 ? (
              <p className="text-gray-500 text-center">Your cart is empty</p>
            ) : (
              <div>
                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {cart.items.map((item) => (
                    <div
                      key={item.id}
                      className="border border-gray-300 rounded-lg p-4 space-y-3"
                    >
                      <div className="flex justify-between items-start">
                        <h3 className="font-medium text-gray-900">
                          {item.name}
                        </h3>
                        <Button
                          variant={'ghost'}
                          className="text-red-500"
                          onClick={() => handleRemoveItem(item.id)}
                        >
                          Remove
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">
                            Quantity:
                          </span>
                          <Input
                            type="text"
                            inputMode="numeric"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              handleQuantityChange(
                                item.id,
                                parseInt(e.target.value) || 1
                              )
                            }
                            className="px-2 w-20"
                          />
                        </div>
                        <div className="space-y-1">
                          <div className="text-sm text-gray-600">
                            Amount: {currencyFormatter.format(item.price)}
                          </div>
                          <div className="font-medium">
                            Sub-total:{' '}
                            {currencyFormatter.format(
                              item.price * item.quantity
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="h-12 border-b  border-gray-300">
                      <tr className="">
                        <th className="text-left font-semibold text-gray-800 px-4 w-[40%]">
                          Product
                        </th>
                        <th className="text-left font-semibold text-gray-800 px-4 w-[15%]">
                          Quantity
                        </th>
                        <th className="text-left font-semibold text-gray-800 px-4 w-[15%]">
                          Amount
                        </th>
                        <th className="text-left font-semibold text-gray-800 px-4 w-[20%]">
                          Sub-total
                        </th>
                        <th className="w-[10%] px-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart.items.map((item, index) => (
                        <tr
                          key={item.id}
                          className={`border-b border-gray-200 last:border-gray-300 transition-colors py-4 ${
                            index % 2 === 1 ? 'bg-gray-50' : ''
                          }`}
                        >
                          <td className="font-medium break-words text-sm text-gray-800 py-3 px-4 w-[40%]">
                            {item.name}
                          </td>
                          <td className="py-3 px-4 w-[15%]">
                            <div className="flex w-auto">
                              <div className="flex">
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item.id,
                                      parseInt(e.target.value) || 1
                                    )
                                  }
                                  className="px-2 w-20"
                                />
                              </div>
                            </div>
                          </td>
                          <td className="text-left whitespace-nowrap text-gray-700 py-3 px-4 w-[15%]">
                            {currencyFormatter.format(item.price)}
                          </td>
                          <td className="text-left whitespace-nowrap text-gray-700 py-3 px-4 w-[20%]">
                            {currencyFormatter.format(
                              item.price * item.quantity
                            )}
                          </td>
                          <td className="text-left py-3 px-4 w-[10%]">
                            <Button
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-sm whitespace-nowrap text-red-600 hover:text-red-700 hover:bg-red-50"
                              variant="ghost"
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Total */}
                <div className="mt-6 md:mt-4 flsex flex-col md:flex-row justify-end items-end md:items-center  pt-4 md:pt-2 gap-4 md:gap-0">
                  {paymentFeeQuery.isFetching ? (
                    <div className="flex py-10 justify-end">
                      <EllipsesLoader size="sm" />
                    </div>
                  ) : (
                    <div className="w-full md:w-auto">
                      <div className="text-right space-y-2">
                        <div className="flex items-center justify-end gap-2 text-right">
                          <div className="text-sm text-gray-800">
                            Amount to provider
                          </div>
                          <div className="font-medium text-gray-800">
                            {currencyFormatter.format(paymentFee.base_amount)}
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 text-right">
                          <div className="text-sm text-gray-800">
                            Service Fee
                          </div>
                          <div className="font-medium text-gray-800">
                            {currencyFormatter.format(
                              getTotalServiceFeeToBePaidByApplicant(
                                paymentFee.service_fee,
                                paymentFee.serviceFeePayer
                              )
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 justify-end">
                          <span className="text-sm text-gray-800">
                            Your Total
                          </span>
                          <div className="font-medium text-xl text-gray-800">
                            {currencyFormatter.format(totalAmount)}
                          </div>
                        </div>
                        <AppDialog
                          open={showPurseSelection}
                          setOpen={setShowPurseSelection}
                          panelClassName="sm:w-auto sm:max-w-max"
                        >
                          <PurseSelectionForm
                            serviceFeeInformation={paymentFee}
                            selectedPurses={[]}
                            apiPurses={purse}
                            onSubmit={onSubmit}
                            onBack={onClosePurseSelection}
                            submitText={'send'}
                          />
                        </AppDialog>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex justify-end -mb-5 mt-2">
              <SubmitButton
                className="mt-2 w-32"
                buttonTxt={translate('checkout')}
                onClick={handleCheckout}
                status={mutation.status}
                forceDisabled={
                  paymentFeeQuery.status === 'pending' ||
                  paymentFeeQuery.status === 'error'
                }
              />
            </div>
          </div>
        )
      }}
    </MultiQueryPageLoader>
  )
}

export default Cart
